import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc, collection, query, where, getDocs, limit } from "firebase/firestore";
import { auth, db } from "../config/firebase";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userOrgId, setUserOrgId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [managerAssignments, setManagerAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          const data = userDoc.data();
          setUserData(data);

          // Phase 5: `users.role` is now a denormalized flag computed from
          // org_managers by Cloud Functions. We still read it directly because
          // Firestore rules use it for "are you any org owner" checks, and
          // matching that contract here keeps role-gated UI consistent with
          // what the rules will actually allow.
          const role = data?.role || (data?.isAdmin ? 'admin' : null);
          setUserRole(role);

          const isPrivileged = role === 'admin' || role === 'superadmin' || data?.isAdmin === true;
          setIsAdmin(isPrivileged);

          // Load this user's org_managers rows. Phase 5 makes org_managers
          // the source of truth for which orgs a user belongs to, so we
          // always load it (not just for non-privileged users like before).
          // Superadmins still pass through OrganizationContext's full-org
          // query — these rows just become a no-op for them.
          try {
            const snap = await getDocs(query(
              collection(db, 'org_managers'),
              where('userId', '==', user.uid),
              limit(50)
            ));
            const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setManagerAssignments(rows);
            // Derive userOrgId for org_owners from the first owner row
            // (replaces the legacy users.organizationId field).
            if (role === 'org_owner') {
              const ownerRow = rows.find(r => r.role === 'owner') || rows[0];
              setUserOrgId(ownerRow?.orgId || null);
            } else {
              setUserOrgId(null);
            }
          } catch (err) {
            if (err?.code !== 'permission-denied') {
              console.error('Error loading manager assignments:', err);
            }
            setManagerAssignments([]);
            setUserOrgId(null);
          }
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsAdmin(false);
          setUserRole(null);
          setUserOrgId(null);
          setUserData(null);
          setManagerAssignments([]);
        }
      } else {
        setIsAdmin(false);
        setUserRole(null);
        setUserOrgId(null);
        setUserData(null);
        setManagerAssignments([]);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Verify user has admin or org_owner access
    const userDoc = await getDoc(doc(db, "users", user.uid));
    const userData = userDoc.data();
    const role = userData?.role || (userData?.isAdmin ? 'admin' : null);

    if (role !== 'admin' && role !== 'superadmin' && role !== 'org_owner' && role !== 'salesperson' && role !== 'affiliate') {
      // Check if user is an org manager
      let isManager = false;
      try {
        const managerCheck = await getDocs(
          query(collection(db, 'org_managers'), where('userId', '==', user.uid), limit(1))
        );
        isManager = !managerCheck.empty;
      } catch (_err) {
        // Permission denied means no org_managers access — not a manager
      }
      if (!isManager) {
        await firebaseSignOut(auth);
        throw new Error("Unauthorized: Admin or Organization Owner access required");
      }
    }

    return userCredential;
  };

  const signOut = () => {
    return firebaseSignOut(auth);
  };

  // Dev role override (development only)
  const [devRoleOverride, setDevRoleOverride] = useState(null);
  const isDev = import.meta.env.DEV;

  const effectiveUserRole = (isDev && devRoleOverride) ? devRoleOverride : userRole;
  const effectiveIsAdmin = (isDev && devRoleOverride)
    ? (devRoleOverride === 'admin' || devRoleOverride === 'superadmin')
    : isAdmin;
  const effectiveIsSalesperson = effectiveUserRole === 'salesperson';

  const value = {
    currentUser,
    isAdmin: effectiveIsAdmin,
    userRole: effectiveUserRole,
    userOrgId,
    userData,
    managerAssignments,
    isSalesperson: effectiveIsSalesperson,
    signIn,
    signOut,
    loading,
    // Dev only
    ...(isDev ? { devRoleOverride, setDevRoleOverride, actualRole: userRole } : {}),
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
